// Tracking → In-flight tab.
//
// The workhorse view. Big filterable table of every shipment that
// isn't yet delivered. Row click opens the right-side drawer with
// the full TrackingTimeline. Filters are URL-encoded so a refresh /
// share keeps them.
//
// Defaults to "everything not delivered" — operators can narrow by
// canonical status, carrier, recipient state, weight band, days-in-
// transit floor, or substring search across order/tracking/city/store.

import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, Search, X, ChevronLeft, ChevronRight, RefreshCw,
  Package, Truck, MapPin, AlertTriangle, PackageCheck, HelpCircle, Globe, Ban,
} from 'lucide-react'
import { format, parseISO, formatDistanceToNow } from 'date-fns'
import { getTrackingInFlight } from '../../lib/api'
import OrderDetailDrawer from './OrderDetailDrawer'

const STATUS_OPTIONS = [
  { value: 'picked_up',        label: 'Picked up'        },
  { value: 'in_transit',       label: 'In transit'       },
  { value: 'at_hub',           label: 'At facility'      },
  { value: 'out_for_delivery', label: 'Out for delivery' },
  { value: 'attempted',        label: 'Attempted'        },
  { value: 'exception',        label: 'Exception'        },
  { value: 'unknown',          label: 'Unknown'          },
]

// Carriers we actually ship through. Each "chip" maps to one OR many
// raw carrier codes — e.g. clicking "Australia Post" sends BOTH
// 'australia_post' AND 'australia_post_mypost_business' to the API
// (single AP customer account covers both product tiers). Keep this
// list in sync with the carriers seen in `orders.carrier` — adding
// Aramex/Sendle/etc here is a one-line change once you start using
// them.
const CARRIER_GROUPS = [
  { key: 'australia_post', label: 'Australia Post',
    codes: ['australia_post', 'australia_post_mypost_business'] },
  { key: 'fedex',          label: 'FedEx',
    codes: ['fedex'] },
  { key: 'dhl',            label: 'DHL',
    codes: ['dhl_ecommerce_au', 'dhl_express_australia'] },
  { key: 'ups',            label: 'UPS',
    codes: ['ups'] },
  // TNT omitted — no tracking integration.
]

const STATE_OPTIONS = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

const STATUS_META = {
  picked_up:                { color: 'bg-slate-100 text-slate-700',     Icon: Package },
  in_transit:               { color: 'bg-blue-50 text-blue-700',        Icon: Truck },
  at_hub:                   { color: 'bg-violet-50 text-violet-700',    Icon: MapPin },
  out_for_delivery:         { color: 'bg-amber-50 text-amber-700',      Icon: Truck },
  attempted:                { color: 'bg-amber-100 text-amber-800',     Icon: AlertTriangle },
  exception:                { color: 'bg-red-50 text-red-700',          Icon: AlertTriangle },
  delivered:                { color: 'bg-emerald-50 text-emerald-700',  Icon: PackageCheck },
  // Derived bucket for international shipments that AP can't surface
  // in-transit events for (handoff to destination carrier).
  international_in_transit: { color: 'bg-cyan-50 text-cyan-700',        Icon: Globe },
  // Terminal, and filtered out server-side — kept here so a row already on
  // screen when the status flips renders a proper pill instead of a blank.
  // Deliberately absent from STATUS_OPTIONS: the filter would always be empty.
  cancelled:                { color: 'bg-slate-100 text-slate-600',     Icon: Ban },
  unknown:                  { color: 'bg-slate-50 text-slate-500',      Icon: HelpCircle },
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unknown
  const Icon = meta.Icon
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium ${meta.color}`}>
      <Icon size={10} />
      {status?.replaceAll('_', ' ') || 'unknown'}
    </span>
  )
}

function timeAgo(iso) {
  if (!iso) return '—'
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }) } catch { return '—' }
}

function fmtDate(iso) {
  if (!iso) return '—'
  try { return format(parseISO(iso), 'd MMM') } catch { return '—' }
}

// ── Multi-select chip group (used for status / carrier / state) ─────────────

function ChipGroup({ label, options, values, onChange }) {
  const set = new Set(values || [])
  const toggle = v => {
    const next = new Set(set)
    if (next.has(v)) next.delete(v); else next.add(v)
    onChange([...next])
  }
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => {
          const value = typeof opt === 'string' ? opt : opt.value
          const text  = typeof opt === 'string' ? opt : opt.label
          const on    = set.has(value)
          return (
            <button
              key={value}
              onClick={() => toggle(value)}
              className={[
                'text-xs px-2 py-0.5 rounded border transition-colors',
                on ? 'bg-blue-600 border-blue-600 text-white'
                   : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
              ].join(' ')}
            >
              {text}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Filter bar (URL-encoded) ────────────────────────────────────────────────

function useFilterParams() {
  // Wraps useSearchParams so each consumer reads a typed object.
  const [params, setParams] = useSearchParams()
  const get = key => params.getAll(key)
  const intl = params.get('international')
  // Destination filter defaults to DOMESTIC — international shipments
  // can't be meaningfully tracked (AusPost hands off to the destination
  // postal carrier) so they're out of the default view. URL encoding:
  //   absent      → domestic (default)
  //   'all'       → no filter (domestic + international mixed)
  //   'true'      → international only
  const destSel = intl === 'all' ? 'all' : intl === 'true' ? 'intl' : 'domestic'
  const filters = {
    status:        get('status'),
    carrier:       get('carrier'),
    state:         get('state'),
    international: intl === 'all' ? undefined : intl === 'true' ? true : false,
    destSel,
    // client_id is owned by the Tracking page shell (global filter
    // above the tabs). The tab just reads it from the URL and includes
    // it in the API call so the table narrows in sync with Overview.
    client_id:     params.get('client_id') || undefined,
    min_days:      params.get('min_days') ? Number(params.get('min_days')) : undefined,
    search:        params.get('search') || '',
    days:          Number(params.get('days') || 30),
    page:          Number(params.get('page') || 1),
  }
  function setFilter(patch) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k)
      if (Array.isArray(v)) v.forEach(x => x != null && next.append(k, String(x)))
      else if (v != null && v !== '') next.set(k, String(v))
    }
    // Anytime a filter changes, reset page to 1 (unless the patch is just paging).
    if (!('page' in patch)) next.delete('page')
    setParams(next, { replace: true })
  }
  return [filters, setFilter]
}

// Toggle a CARRIER_GROUPS chip: adds/removes ALL underlying carrier
// codes for that group from the current carrier filter. A group is
// considered "on" when every one of its codes is in the current list.
function carrierGroupOn(filters, group) {
  return group.codes.every(c => filters.carrier.includes(c))
}
function toggleCarrierGroup(filters, setFilter, group) {
  const set = new Set(filters.carrier)
  const allOn = carrierGroupOn(filters, group)
  if (allOn) group.codes.forEach(c => set.delete(c))
  else       group.codes.forEach(c => set.add(c))
  setFilter({ carrier: [...set] })
}

// ── Main tab ────────────────────────────────────────────────────────────────

export default function TrackingInFlightTab() {
  const qc = useQueryClient()
  const [filters, setFilter] = useFilterParams()
  const [drawerRow, setDrawerRow] = useState(null)
  const [searchInput, setSearchInput] = useState(filters.search)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['tracking', 'in-flight', filters],
    queryFn: () => getTrackingInFlight({
      days:          filters.days,
      status:        filters.status,
      carrier:       filters.carrier,
      state:         filters.state,
      international: filters.international,
      client_id:     filters.client_id,
      min_days:      filters.min_days,
      search:        filters.search || undefined,
      page:          filters.page,
      page_size:     50,
    }),
    refetchInterval: 60_000,
    keepPreviousData: true,
  })

  const rows = data?.rows || []
  const total = data?.total || 0
  const pageSize = data?.page_size || 50
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  const activeFilterCount = useMemo(() => (
    (filters.status.length > 0 ? 1 : 0) +
    (filters.carrier.length > 0 ? 1 : 0) +
    (filters.state.length > 0 ? 1 : 0) +
    // Domestic is the default, so only count Destination as an active
    // filter when the user has moved away from it.
    (filters.destSel !== 'domestic' ? 1 : 0) +
    (filters.min_days ? 1 : 0) +
    (filters.search ? 1 : 0)
  ), [filters])

  return (
    <div>
      {/* Filter bar */}
      <div className="card p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <ChipGroup
            label="Status"
            options={STATUS_OPTIONS}
            values={filters.status}
            onChange={v => setFilter({ status: v })}
          />
          {/* Carrier — custom because each chip can expand to multiple
              underlying codes (Australia Post → AP eParcel + MyPost
              Business; DHL → eCommerce + Express). */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Carrier</p>
            <div className="flex flex-wrap gap-1">
              {CARRIER_GROUPS.map(g => {
                const on = carrierGroupOn(filters, g)
                return (
                  <button
                    key={g.key}
                    onClick={() => toggleCarrierGroup(filters, setFilter, g)}
                    className={[
                      'text-xs px-2 py-0.5 rounded border transition-colors',
                      on ? 'bg-blue-600 border-blue-600 text-white'
                         : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
                    ].join(' ')}
                  >
                    {g.label}
                  </button>
                )
              })}
            </div>
          </div>
          <ChipGroup
            label="Destination state"
            options={STATE_OPTIONS}
            values={filters.state}
            onChange={v => setFilter({ state: v })}
          />
          {/* Destination — defaults to Domestic. International shipments
              aren't trackable past the AU handoff, so they're off by
              default but reachable via this toggle. */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Destination</p>
            <div className="inline-flex rounded border border-slate-200 overflow-hidden text-xs bg-white">
              {[
                { sel: 'domestic', label: 'Domestic',      set: undefined },
                { sel: 'all',      label: 'All',           set: 'all'     },
                { sel: 'intl',     label: 'International',  set: 'true'    },
              ].map(opt => (
                <button
                  key={opt.sel}
                  onClick={() => setFilter({ international: opt.set })}
                  className={[
                    'px-2.5 py-0.5 font-medium transition-colors',
                    filters.destSel === opt.sel
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Min days in transit</p>
            <input
              type="number"
              min="0"
              max="60"
              value={filters.min_days ?? ''}
              onChange={e => setFilter({ min_days: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="—"
              className="w-20 text-xs px-2 py-1 border border-slate-200 rounded"
            />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Window</p>
            <select
              value={filters.days}
              onChange={e => setFilter({ days: Number(e.target.value) })}
              className="text-xs px-2 py-1 border border-slate-200 rounded bg-white"
            >
              {[7, 30, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        </div>

        {/* Search + clear */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setFilter({ search: searchInput }) }}
              onBlur={() => setFilter({ search: searchInput })}
              placeholder="Order #, tracking, city, postcode, store…"
              className="w-full text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded"
            />
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={() => { setSearchInput(''); setFilter({ status: [], carrier: [], state: [], international: undefined, min_days: undefined, search: '' }) }}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors"
            >
              <X size={11} /> Clear ({activeFilterCount})
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['tracking', 'in-flight'] })}
            disabled={isFetching}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 disabled:opacity-50"
            title="Refetch the list"
          >
            {isFetching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between text-xs text-slate-500 mb-2 px-1">
        <p>
          {isLoading
            ? 'Loading…'
            : <>Showing <span className="font-medium text-slate-700">{rows.length}</span> of <span className="font-medium text-slate-700">{total}</span> in-flight shipments</>}
        </p>
        <PageControls
          page={filters.page}
          pageCount={pageCount}
          onChange={p => setFilter({ page: p })}
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Order</th>
                <th className="px-3 py-2 text-left font-semibold">Client</th>
                <th className="px-3 py-2 text-left font-semibold">Carrier</th>
                <th className="px-3 py-2 text-left font-semibold">Tracking</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Last scan</th>
                <th className="px-3 py-2 text-left font-semibold">Destination</th>
                <th className="px-3 py-2 text-right font-semibold">Shipped</th>
                <th className="px-3 py-2 text-right font-semibold">In transit</th>
                <th className="px-3 py-2 text-right font-semibold">Wt.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 10 }).map((_, j) => (
                      <td key={j} className="px-3 py-2"><div className="skeleton h-3 rounded" style={{ width: '70px' }} /></td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-10 text-center text-slate-400">
                    No in-flight shipments match these filters.
                  </td>
                </tr>
              ) : rows.map(r => (
                <tr
                  key={r.order_id}
                  onClick={() => setDrawerRow(r)}
                  className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-2 font-medium text-slate-700">{r.order_number || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <div className="leading-tight">
                      <p>{r.client_name || '—'}</p>
                      {r.store_name && <p className="text-[10px] text-slate-400">{r.store_name}</p>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.carrier || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[10.5px] text-slate-600">{r.tracking_number || '—'}</td>
                  <td className="px-3 py-2"><StatusPill status={r.tracking_status} /></td>
                  <td className="px-3 py-2 text-slate-600">
                    <div className="leading-tight">
                      <p>{timeAgo(r.last_scan_at)}</p>
                      {r.last_scan_location && <p className="text-[10px] text-slate-400">{r.last_scan_location}</p>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {r.recipient_suburb ? `${r.recipient_suburb}, ${r.recipient_state}` : r.recipient_state || '—'}
                    {r.recipient_postcode && <span className="text-[10px] text-slate-400 ml-1">{r.recipient_postcode}</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{fmtDate(r.shipped_date)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.days_in_transit > 7 ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                    {r.days_in_transit != null ? `${r.days_in_transit}d` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500 tabular-nums">
                    {r.weight_kg != null ? `${r.weight_kg.toFixed(1)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      <OrderDetailDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />
    </div>
  )
}

function PageControls({ page, pageCount, onChange }) {
  if (pageCount <= 1) return null
  return (
    <div className="inline-flex items-center gap-1">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
      >
        <ChevronLeft size={14} />
      </button>
      <span className="text-xs text-slate-500 px-1">
        {page} / {pageCount}
      </span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page >= pageCount}
        className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  )
}
